import { useState, useEffect, useCallback } from "react";

// NOTE: This stores ONLY non-sensitive data — the uploaded book list and the
// public catalog-availability results (progress/checkedAt) so a search can be
// resumed after a reload. No credentials, tokens, or PINs are ever stored here;
// library credentials live encrypted on the server (see backend get_stored_creds).
const STORAGE_KEY = "acpl-checker-session";

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to load saved session:", e);
    return null;
  }
}

function saveSession(session) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (e) {
    // Typically quota exceeded or private-mode restrictions — non-fatal.
    console.warn("Failed to persist session (storage unavailable):", e);
  }
}

function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("Failed to clear saved session:", e);
  }
}

export function usePersistedSession() {
  const [savedSession, setSavedSession] = useState(null);

  useEffect(() => {
    setSavedSession(loadSession());
  }, []);

  const persist = useCallback((session) => {
    saveSession(session);
    setSavedSession(session);
  }, []);

  const clear = useCallback(() => {
    clearSession();
    setSavedSession(null);
  }, []);

  return { savedSession, persist, clear };
}
